import { useCallback, useEffect, useState } from "react";

export type AsyncState<T> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; value: T }
  | { status: "error"; error: Error };

type Settled<T> = { key: string; ok: true; value: T } | { key: string; ok: false; error: Error };

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

/**
 * Runs `load` whenever `key` changes (`null` = idle). Loading state is derived
 * from whether the latest settled result belongs to the current key, so no
 * state is set synchronously and stale responses are ignored.
 *
 * `reload` re-runs `load` for the current key and resolves to success.
 */
export function useAsync<T>(
  key: string | null,
  load: () => Promise<T>,
): [AsyncState<T>, () => Promise<boolean>] {
  const [settled, setSettled] = useState<Settled<T> | null>(null);

  useEffect(() => {
    if (key === null) return;
    let current = true;
    load().then(
      (value) => current && setSettled({ key, ok: true, value }),
      (error: unknown) => current && setSettled({ key, ok: false, error: toError(error) }),
    );
    return () => {
      current = false;
    };
    // `load` is keyed by `key`: callers encode every input in the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const reload = useCallback(async (): Promise<boolean> => {
    if (key === null) return false;
    try {
      const value = await load();
      setSettled({ key, ok: true, value });
      return true;
    } catch (error) {
      setSettled({ key, ok: false, error: toError(error) });
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (key === null) return [{ status: "idle" }, reload];
  if (!settled || settled.key !== key) return [{ status: "loading" }, reload];
  return [
    settled.ok
      ? { status: "ready", value: settled.value }
      : { status: "error", error: settled.error },
    reload,
  ];
}
